// ── Weapon Pickup ── Client-side weapon drop entities

import * as THREE from 'three';
import { WEAPONS, type WeaponId } from '../core/config';
import { loadWeaponWorldModel } from './weapon-world-models';

export interface WeaponDropEntity {
  id: number;
  weaponId: string;
  position: THREE.Vector3;
  mesh: THREE.Group;
}

const MAX_DROPS = 32;
const DEFAULT_PICKUP_RANGE = 2.0;

// Color mapping by weapon type
const TYPE_COLORS: Record<string, number> = {
  pistol: 0xffdd33,   // yellow
  rifle: 0xff8833,    // orange
  sniper: 0xff3333,   // red
  smg: 0x33cc33,      // green
  shotgun: 0x885533,  // brown
  melee: 0xffffff,    // white (knife)
};

// Animation constants
const BOB_AMPLITUDE = 0.1;   // 0.1m bob height
const BOB_FREQUENCY = 1.0;   // 1Hz
const ROTATION_SPEED = Math.PI / 4; // 45 deg/s

function getWeaponColor(weaponId: string): number {
  const cfg = WEAPONS[weaponId as WeaponId];
  if (!cfg) return 0xcccccc;
  return TYPE_COLORS[cfg.type] ?? 0xcccccc;
}

function getWeaponName(weaponId: string): string {
  const cfg = WEAPONS[weaponId as WeaponId];
  return cfg?.name ?? weaponId;
}

function disposeMeshTree(obj: THREE.Object3D): void {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry?.dispose();
      if (Array.isArray(child.material)) {
        child.material.forEach((m) => m.dispose());
      } else if (child.material) {
        child.material.dispose();
      }
    }
  });
}

function createDropMesh(weaponId: string): THREE.Group {
  const group = new THREE.Group();
  group.name = `weapon_drop_${weaponId}`;

  const color = getWeaponColor(weaponId);

  // Box mesh representing the weapon
  const boxGeo = new THREE.BoxGeometry(0.6, 0.15, 0.15);
  const boxMat = new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: 0.3,
    roughness: 0.4,
    metalness: 0.6,
  });
  const box = new THREE.Mesh(boxGeo, boxMat);
  box.castShadow = true;
  group.add(box);

  // Store the initial Y for bob animation
  group.userData.baseY = 0;
  group.userData.elapsed = 0;

  return group;
}

export class WeaponDropManager {
  private drops: Map<number, WeaponDropEntity> = new Map();
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  /** Add a weapon drop from server event. */
  addDrop(id: number, weaponId: string, x: number, y: number, z: number): void {
    // Remove existing drop with same id (replacement)
    if (this.drops.has(id)) {
      this.removeDrop(id);
    }

    // Enforce max drops — remove oldest if exceeded
    while (this.drops.size >= MAX_DROPS) {
      const oldestId = this.drops.keys().next().value!;
      this.removeDrop(oldestId);
    }

    const mesh = createDropMesh(weaponId);
    const position = new THREE.Vector3(x, y, z);
    mesh.position.copy(position);
    mesh.userData.baseY = y;
    mesh.userData.elapsed = 0;
    mesh.userData.glbReqId = 0;

    const entity: WeaponDropEntity = { id, weaponId, position, mesh };
    this.drops.set(id, entity);
    this.scene.add(mesh);

    // W7: try to swap the procedural box for the real GLB once the model is
    // available. If the load fails / no GLB is configured, the box stays.
    const reqId = ++mesh.userData.glbReqId;
    void loadWeaponWorldModel(weaponId as WeaponId).then((glb) => {
      // Drop may have been removed before the load completed; bail out if so.
      if (!this.drops.has(id) || mesh.userData.glbReqId !== reqId || !glb) return;
      // Remove placeholder children, keep the entity Group so that bob/spin
      // animation state on userData survives the swap.
      while (mesh.children.length > 0) {
        const child = mesh.children[0];
        mesh.remove(child);
        disposeMeshTree(child);
      }
      // The shared cache already pre-applies the manifest scale/rotation, but
      // dropped weapons need a different uplift than the FPV viewmodel — they
      // sit on the ground and are rotated to read clearly from any side. We
      // re-scale up so the ground prop reads at ~0.6m, matching the prior box.
      glb.scale.multiplyScalar(8);
      glb.position.set(0, 0, 0);
      mesh.add(glb);
    });
  }

  /** Remove a weapon drop (picked up or despawned). */
  removeDrop(id: number): void {
    const entity = this.drops.get(id);
    if (!entity) return;

    this.scene.remove(entity.mesh);
    // Dispose mesh resources
    entity.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach((m) => m.dispose());
        } else if (child.material) {
          child.material.dispose();
        }
      }
    });
    this.drops.delete(id);
  }

  /** Update visual effects (gentle hover/rotation animation). */
  update(dt: number): void {
    for (const entity of this.drops.values()) {
      const mesh = entity.mesh;
      mesh.userData.elapsed += dt;
      const t = mesh.userData.elapsed as number;

      // Bob animation
      const baseY = mesh.userData.baseY as number;
      mesh.position.y = baseY + Math.sin(t * BOB_FREQUENCY * Math.PI * 2) * BOB_AMPLITUDE;

      // Rotation animation
      mesh.rotation.y += ROTATION_SPEED * dt;

      // entity.position.y is the spawn Y; pickup distance uses base Y (not bob-animated)
    }
  }

  /** Get nearest drop within pickup range of a position. */
  getNearestDrop(position: THREE.Vector3, maxRange: number = DEFAULT_PICKUP_RANGE): WeaponDropEntity | null {
    let nearest: WeaponDropEntity | null = null;
    let nearestDist = maxRange;

    for (const entity of this.drops.values()) {
      const dx = entity.position.x - position.x;
      const dy = entity.position.y - position.y;
      const dz = entity.position.z - position.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = entity;
      }
    }

    return nearest;
  }

  /** Remove all drops (round end). */
  clear(): void {
    for (const [id] of this.drops) {
      this.removeDrop(id);
    }
  }

  /** Dispose all resources. */
  dispose(): void {
    this.clear();
  }

  /** Get count of active drops (for testing). */
  get count(): number {
    return this.drops.size;
  }
}
